import { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Checkbox, Collapse, Box, IconButton, Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import type { UserResponse } from '../api/types';
import { formatDateTime } from '../utils/format';
import { UserNodePanel } from './UserNodePanel';

interface Props {
  users: UserResponse[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function UserTable({ users, selectedIds, onSelectionChange }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const allSelected = users.length > 0 && selectedIds.length === users.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) onSelectionChange([]);
    else onSelectionChange(users.map(u => u.id));
  };

  const toggleOne = (id: string) => {
    if (selectedIds.includes(id)) onSelectionChange(selectedIds.filter(x => x !== id));
    else onSelectionChange([...selectedIds, id]);
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (users.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        暂无用户。点击「添加用户」预注册，或通过「批量导入」快速录入。
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={toggleAll}
                size="small"
              />
            </TableCell>
            <TableCell width={40}></TableCell>
            <TableCell>用户名</TableCell>
            <TableCell>显示名</TableCell>
            <TableCell>邮箱</TableCell>
            <TableCell>部门</TableCell>
            <TableCell>角色</TableCell>
            <TableCell>最后登录</TableCell>
            <TableCell>状态</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(user => {
            const isPreRegistered = !user.lastLoginAt;
            const isExpanded = expandedId === user.id;
            return (
              <>
                <TableRow
                  key={user.id}
                  hover
                  selected={selectedIds.includes(user.id)}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => toggleExpand(user.id)}
                >
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(user.id)}
                      onChange={() => toggleOne(user.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small">
                      {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {user.synoUsername}
                      {isPreRegistered && (
                        <Chip label="待登录" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.displayName}</TableCell>
                  <TableCell>{user.email || '—'}</TableCell>
                  <TableCell>{user.department || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isAdmin ? '管理员' : '员工'}
                      size="small"
                      color={user.isAdmin ? 'primary' : 'default'}
                      variant={user.isAdmin ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '从未登录'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? '启用' : '禁用'}
                      size="small"
                      color={user.isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow key={`${user.id}-expand`}>
                    <TableCell colSpan={9} sx={{ p: 0, borderBottom: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <UserNodePanel userId={user.id} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
